(() => {
  const usdEstimateNodes = () => Array.from(document.querySelectorAll("[data-selivia-usd-estimate]"));
  const usdRateCacheKey = "selivia-cad-usd-rate";
  const usdRateEndpoint = "https://api.frankfurter.dev/v1/latest?base=CAD&symbols=USD";
  const usdRateMaxAge = 12 * 60 * 60 * 1000;
  let usdRatePromise = null;

  const readCachedUsdRate = () => {
    try {
      const cached = JSON.parse(localStorage.getItem(usdRateCacheKey) || "null");
      if (cached && cached.rate && Date.now() - cached.savedAt < usdRateMaxAge) {
        return cached;
      }
    } catch (error) {
      return null;
    }
    return null;
  };

  const fetchUsdRate = () => {
    if (usdRatePromise) return usdRatePromise;

    usdRatePromise = (async () => {
      const cached = readCachedUsdRate();
      if (cached) return cached;

      try {
        const response = await fetch(usdRateEndpoint, { cache: "no-store" });
        if (!response.ok) throw new Error("Unable to fetch exchange rate");
        const data = await response.json();
        const rate = Number(data?.rates?.USD);
        if (!rate) throw new Error("Missing USD exchange rate");
        const fresh = {
          rate,
          date: data.date || "",
          savedAt: Date.now(),
          source: "daily market rate"
        };
        try {
          localStorage.setItem(usdRateCacheKey, JSON.stringify(fresh));
        } catch (error) {
          /* Private browsing can block localStorage. */
        }
        return fresh;
      } catch (error) {
        const fallbackNode = usdEstimateNodes()[0];
        return {
          rate: Number(fallbackNode?.dataset.fallbackRate || 0.7138),
          date: "",
          savedAt: Date.now(),
          source: "recent fallback rate"
        };
      }
    })();

    return usdRatePromise;
  };

  const formatUsd = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(value);

  const renderUsdEstimates = () => {
    const nodes = usdEstimateNodes();
    if (!nodes.length) return;

    fetchUsdRate().then(({ rate, date, source }) => {
      nodes.forEach((node) => {
        const cents = Number(node.dataset.cadCents || 0);
        const renderedKey = `${cents}-${rate}`;
        if (!cents || node.dataset.renderedKey === renderedKey) return;

        const usdValue = (cents / 100) * rate;
        const output = node.querySelector("[data-selivia-usd-output]");
        if (output) {
          output.textContent = `Approx. ${formatUsd(usdValue)} USD`;
        }

        const note = node.querySelector(".slv-usd-estimate__note");
        if (note) {
          note.textContent = "Converted to USD by PayPal or your card issuer at checkout.";
        }

        node.dataset.renderedKey = renderedKey;
        node.hidden = false;
      });
    });
  };

  renderUsdEstimates();

  let slvCartRefreshTimer = null;
  const scheduleCartWidgetRefresh = () => {
    if (slvCartRefreshTimer) return;
    slvCartRefreshTimer = window.setTimeout(() => {
      slvCartRefreshTimer = null;
      if (document.querySelector("[data-slv-shipping-meter]")) refreshShippingMeters();
      if (document.querySelector("[data-slv-cart-upsell]")) refreshCartUpsell();
    }, 400);
  };

  const estimateObserver = new MutationObserver(() => {
    window.requestAnimationFrame(renderUsdEstimates);
    scheduleCartWidgetRefresh();
  });
  estimateObserver.observe(document.documentElement, { childList: true, subtree: true });


  document.querySelectorAll("[data-selivia-popup]").forEach((popup) => {
    const id = popup.dataset.seliviaPopup;
    const delay = Number(popup.dataset.delay || 2200);
    const close = () => {
      popup.classList.remove("is-active");
      try {
        sessionStorage.setItem(`selivia-popup-${id}`, "closed");
      } catch (error) {
        /* Storage can be disabled in preview browsers. */
      }
    };

    popup.querySelectorAll("[data-popup-close]").forEach((button) => {
      button.addEventListener("click", close);
    });

    let wasClosed = false;
    try {
      wasClosed = sessionStorage.getItem(`selivia-popup-${id}`) === "closed";
    } catch (error) {
      wasClosed = false;
    }

    if (!wasClosed) {
      window.setTimeout(() => popup.classList.add("is-active"), delay);
    }

    const timer = popup.querySelector("[data-popup-timer]");
    if (timer) {
      let seconds = Number(timer.dataset.minutes || 15) * 60;
      window.setInterval(() => {
        seconds = Math.max(0, seconds - 1);
        const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
        const remainder = String(seconds % 60).padStart(2, "0");
        timer.textContent = `${minutes}:${remainder}`;
      }, 1000);
    }
  });

  const refreshShippingMeters = () => {
    const meters = document.querySelectorAll("[data-slv-shipping-meter]");
    if (!meters.length || !window.routes) return;

    fetch(`${window.routes.cart_url}.js`, { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => {
        meters.forEach((meter) => {
          const threshold = Number(meter.dataset.slvShippingThreshold || 9500);
          const remaining = Math.max(0, threshold - cart.total_price);
          const percent = Math.max(0, Math.min(100, Math.round((cart.total_price / threshold) * 100)));
          const fill = meter.querySelector(".slv-meter__fill");
          const text = meter.querySelector("[data-slv-shipping-text]");
          if (fill) fill.style.setProperty("--slv-meter", `${percent}%`);
          if (text) {
            text.textContent =
              remaining > 0
                ? `${formatMoneyCents(remaining)} away from free standard shipping.`
                : "Free standard shipping unlocked at checkout.";
          }
        });
      })
      .catch(() => {
        /* Leave server-rendered value in place if the cart fetch fails. */
      });
  };

  function formatMoneyCents(cents) {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "CAD" }).format(cents / 100);
  }

  if (document.querySelectorAll("[data-slv-shipping-meter]").length) {
    refreshShippingMeters();
  }

  const seliviaCartUpsellState = { productIds: new Set() };

  const renderCartUpsellCards = (container, products) => {
    const list = container.querySelector("[data-slv-cart-upsell-list]");
    if (!list) return;

    if (!products.length) {
      container.hidden = true;
      return;
    }

    container.hidden = false;
    list.innerHTML = products
      .map((product) => {
        const variant = product.variants && product.variants[0];
        if (!variant) return "";
        const image = product.featured_image || (product.images && product.images[0]) || "";
        return `
          <div class="slv-cart-upsell__card">
            ${image ? `<img src="${image}&width=160" alt="${product.title.replace(/"/g, "&quot;")}" loading="lazy" width="56" height="56">` : ""}
            <div class="slv-cart-upsell__info">
              <p class="slv-cart-upsell__title">${product.title}</p>
              <p class="slv-cart-upsell__price">${formatMoneyCents(variant.price)}</p>
            </div>
            <button type="button" class="slv-cart-upsell__add" data-slv-cart-upsell-add data-variant-id="${variant.id}">
              Add
            </button>
          </div>
        `;
      })
      .join("");
  };

  const refreshCartUpsell = () => {
    const containers = document.querySelectorAll("[data-slv-cart-upsell]");
    if (!containers.length || !window.routes) return;

    fetch(`${window.routes.cart_url}.js`, { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => {
        seliviaCartUpsellState.productIds = new Set(cart.items.map((item) => item.product_id));
        const anchorId = cart.items.length ? cart.items[cart.items.length - 1].product_id : null;

        containers.forEach((container) => {
          if (!cart.items.length || !anchorId) {
            container.hidden = true;
            return;
          }

          const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
          fetch(`${rootPath}recommendations/products.json?product_id=${anchorId}&limit=6&intent=complementary`)
            .then((response) => response.json())
            .then((data) => {
              const products = (data.products || [])
                .filter((product) => !seliviaCartUpsellState.productIds.has(product.id))
                .slice(0, 3);
              renderCartUpsellCards(container, products);
            })
            .catch(() => {
              container.hidden = true;
            });
        });
      })
      .catch(() => {});
  };

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-slv-cart-upsell-add]");
    if (!button) return;

    button.disabled = true;
    button.textContent = "Adding...";

    fetch(window.routes.cart_add_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: Number(button.dataset.variantId), quantity: 1 })
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          button.disabled = false;
          button.textContent = "Add";
          return;
        }
        const openDrawer = () => {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer && typeof cartDrawer.open === "function") cartDrawer.open();
        };

        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "selivia-cart-upsell",
            productVariantId: response.variant_id,
            cartData: response
          }).then(openDrawer);
        } else {
          openDrawer();
        }
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = "Add";
      });
  });

  if (document.querySelectorAll("[data-slv-cart-upsell]").length) {
    refreshCartUpsell();
  }

  const buildDescriptionAccordion = (description) => {
    const headings = Array.from(description.querySelectorAll("h1, h2, h3, h4"));
    if (!headings.length) return;

    const caretTemplate = document.getElementById("slv-accordion-caret");
    const caretHTML = caretTemplate ? caretTemplate.innerHTML : "";

    const intro = document.createDocumentFragment();
    let node = description.firstChild;
    while (node && node !== headings[0]) {
      const next = node.nextSibling;
      intro.appendChild(node);
      node = next;
    }

    const group = document.createElement("div");
    group.className = "slv-description-accordion quick-add-hidden";

    headings.forEach((heading, index) => {
      const details = document.createElement("details");
      details.className = "product__accordion accordion";
      // Left permanently "open" so the browser's own UA stylesheet never hides
      // the content; visibility is instead driven by max-height below, which
      // lets us animate it and keep exactly one panel open without a snap/jump.
      details.setAttribute("open", "");
      if (index !== 0) details.dataset.slvClosed = "true";

      const summary = document.createElement("summary");
      summary.innerHTML = `<div class="summary__title"><h2 class="h4 accordion__title inline-richtext">${heading.textContent}</h2></div>${caretHTML}`;

      const content = document.createElement("div");
      content.className = "accordion__content rte";

      const nextHeading = headings[index + 1];
      let sibling = heading.nextSibling;
      while (sibling && sibling !== nextHeading) {
        const toMove = sibling;
        sibling = sibling.nextSibling;
        content.appendChild(toMove);
      }

      details.appendChild(summary);
      details.appendChild(content);
      group.appendChild(details);
    });

    description.innerHTML = "";
    description.appendChild(intro);
    description.appendChild(group);
    description.dataset.slvAccordioned = "true";
    initSmoothAccordionGroup(group);
  };

  const initSmoothAccordionGroup = (group) => {
    const items = Array.from(group.children).filter((el) => el.tagName === "DETAILS");

    const setState = (details, isOpen, animate) => {
      const content = details.querySelector(":scope > .accordion__content");
      if (!content) return;

      content.style.overflow = "hidden";
      content.style.transition = animate ? "max-height 0.28s ease" : "none";

      if (isOpen) {
        delete details.dataset.slvClosed;
        content.style.maxHeight = `${content.scrollHeight}px`;
      } else {
        details.dataset.slvClosed = "true";
        content.style.maxHeight = `${content.scrollHeight}px`;
        requestAnimationFrame(() => {
          content.style.maxHeight = "0px";
        });
      }
    };

    items.forEach((details) => {
      const content = details.querySelector(":scope > .accordion__content");
      if (content) content.style.maxHeight = details.dataset.slvClosed ? "0px" : `${content.scrollHeight}px`;

      const summary = details.querySelector(":scope > summary");
      if (!summary) return;

      summary.addEventListener("click", (event) => {
        event.preventDefault();
        const opening = Boolean(details.dataset.slvClosed);

        items.forEach((other) => {
          if (other !== details && !other.dataset.slvClosed) setState(other, false, true);
        });

        setState(details, opening, true);
      });
    });
  };

  document.querySelectorAll("[data-slv-auto-accordion]:not([data-slv-accordioned])").forEach(buildDescriptionAccordion);

    const quizRoot = document.querySelector("[data-selivia-quiz]");

  if (quizRoot) {
    const STEP_ORDER = ["consent", "concern", "timing", "effort", "sensitivity", "contact", "results"];
    const QUESTION_STEPS = ["concern", "timing", "effort", "sensitivity"];
    const storageKey = "seliviaQuizAnswers";
    const state = { concern: null, timing: null, effort: null, sensitivity: null };

    const steps = Array.from(quizRoot.querySelectorAll("[data-quiz-step]"));
    const getStep = (name) => steps.find((el) => el.dataset.quizStep === name);
    const progressWrap = quizRoot.querySelector("[data-quiz-progress]");
    const progressFill = quizRoot.querySelector("[data-quiz-progress-fill]");
    const progressLabel = quizRoot.querySelector("[data-quiz-progress-label]");

    const showStep = (name) => {
      steps.forEach((el) => el.classList.toggle("is-active", el.dataset.quizStep === name));

      const questionIndex = QUESTION_STEPS.indexOf(name);
      if (questionIndex > -1 && progressWrap) {
        progressWrap.hidden = false;
        const percent = Math.round(((questionIndex + 1) / QUESTION_STEPS.length) * 100);
        if (progressFill) progressFill.style.setProperty("--slv-quiz-progress", `${percent}%`);
        if (progressLabel) progressLabel.textContent = `Question ${questionIndex + 1} of ${QUESTION_STEPS.length}`;
      } else if (progressWrap) {
        progressWrap.hidden = true;
      }
    };

    const openQuiz = () => {
      quizRoot.classList.add("is-active");
      quizRoot.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
    };

    const closeQuiz = () => {
      quizRoot.classList.remove("is-active");
      quizRoot.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    };

    const resetQuiz = () => {
      Object.keys(state).forEach((key) => {
        state[key] = null;
      });
      quizRoot.querySelectorAll(".slv-quiz__option").forEach((button) => button.classList.remove("is-selected"));
      quizRoot.querySelectorAll("[data-quiz-continue]").forEach((button) => {
        button.disabled = true;
      });
      const consentCheckbox = quizRoot.querySelector("[data-quiz-consent-checkbox]");
      const consentContinue = quizRoot.querySelector("[data-quiz-consent-continue]");
      if (consentCheckbox) consentCheckbox.checked = false;
      if (consentContinue) consentContinue.disabled = true;
      showStep("consent");
    };

    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-open-routine-quiz]")) {
        event.preventDefault();
        resetQuiz();
        openQuiz();
      }
    });

    const closeButton = quizRoot.querySelector("[data-quiz-close]");
    if (closeButton) closeButton.addEventListener("click", closeQuiz);

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && quizRoot.classList.contains("is-active")) closeQuiz();
    });

    const consentCheckbox = quizRoot.querySelector("[data-quiz-consent-checkbox]");
    const consentContinue = quizRoot.querySelector("[data-quiz-consent-continue]");
    if (consentCheckbox && consentContinue) {
      consentCheckbox.addEventListener("change", () => {
        consentContinue.disabled = !consentCheckbox.checked;
      });
      consentContinue.addEventListener("click", () => showStep("concern"));
    }

    QUESTION_STEPS.forEach((stepName) => {
      const stepEl = getStep(stepName);
      if (!stepEl) return;

      const options = Array.from(stepEl.querySelectorAll("[data-quiz-answer]"));
      const continueButton = stepEl.querySelector("[data-quiz-continue]");

      options.forEach((button) => {
        button.addEventListener("click", () => {
          options.forEach((other) => other.classList.remove("is-selected"));
          button.classList.add("is-selected");
          state[stepName] = button.dataset.quizAnswer;
          if (continueButton) continueButton.disabled = false;
        });
      });

      if (continueButton) {
        continueButton.addEventListener("click", () => {
          const nextStep = STEP_ORDER[STEP_ORDER.indexOf(stepName) + 1];
          if (nextStep) showStep(nextStep);
        });
      }
    });

    quizRoot.querySelectorAll("[data-quiz-back]").forEach((button) => {
      button.addEventListener("click", () => {
        const stepEl = button.closest("[data-quiz-step]");
        const previousStep = STEP_ORDER[STEP_ORDER.indexOf(stepEl.dataset.quizStep) - 1];
        if (previousStep) showStep(previousStep);
      });
    });

    const contactForm = quizRoot.querySelector(".slv-quiz__form");
    const tagsField = quizRoot.querySelector("[data-quiz-tags-field]");
    if (contactForm && tagsField) {
      contactForm.addEventListener("submit", () => {
        const tags = ["quiz-lead"];
        if (state.concern) tags.push(`quiz-concern-${state.concern}`);
        if (state.timing) tags.push(`quiz-timing-${state.timing}`);
        if (state.effort) tags.push(`quiz-effort-${state.effort}`);
        if (state.sensitivity) tags.push(`quiz-sensitivity-${state.sensitivity}`);
        tagsField.value = tags.join(", ");

        try {
          sessionStorage.setItem(storageKey, JSON.stringify(state));
        } catch (error) {
          /* Private browsing can block sessionStorage; the quiz still submits,
             it just won't be able to reopen on the results step after reload. */
        }
        /* No preventDefault: this is a real Shopify customer-form submit that
           reloads the page with ?customer_posted=true, same mechanism as the
           newsletter signup form elsewhere on the site. */
      });
    }

    const retakeButton = quizRoot.querySelector("[data-quiz-retake]");
    if (retakeButton) {
      retakeButton.addEventListener("click", () => {
        resetQuiz();
        openQuiz();
      });
    }

        const cartUrl = quizRoot.dataset.cartUrl || "/cart";

    const refreshCartBubble = () => {
      fetch("/?section_id=cart-icon-bubble")
        .then((response) => (response.ok ? response.text() : null))
        .then((html) => {
          if (!html) return;
          const existing = document.getElementById("cart-icon-bubble");
          const parsed = new DOMParser().parseFromString(html, "text/html");
          const updated = parsed.getElementById("cart-icon-bubble");
          if (existing && updated) existing.innerHTML = updated.innerHTML;
        })
        .catch(() => {
          /* Cosmetic only: header cart count just won't live-update if this fails. */
        });
    };

    quizRoot.querySelectorAll("[data-quiz-add-to-cart]").forEach((button) => {
      button.addEventListener("click", () => {
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        const statusEl = button.closest(".slv-quiz__result-info")?.querySelector("[data-quiz-cart-status]");
        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = "Adding...";

        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
          .then((response) => {
            if (!response.ok) return response.json().then((data) => Promise.reject(data));
            return response.json();
          })
          .then(() => {
            button.textContent = "Added";
            if (statusEl) {
              statusEl.hidden = false;
              statusEl.innerHTML = `Added to cart - <a href="${cartUrl}">View cart</a>`;
            }
            refreshCartBubble();
          })
          .catch((error) => {
            button.disabled = false;
            button.textContent = originalLabel;
            if (statusEl) {
              statusEl.hidden = false;
              statusEl.textContent = (error && error.description) || "Something went wrong - please try again.";
            }
          });
      });
    });

const renderResults = (savedAnswers) => {
      const heading = quizRoot.querySelector("[data-quiz-results-heading]");
      const intro = quizRoot.querySelector("[data-quiz-results-intro]");
      const cards = Array.from(quizRoot.querySelectorAll("[data-quiz-result-card]"));

      const concernKey = savedAnswers.concern || "general";
      const matched =
        cards.find((card) => card.dataset.quizResultCard === concernKey) ||
        cards.find((card) => card.dataset.quizResultCard === "general");

      cards.forEach((card) => {
        card.hidden = card !== matched;
      });

      if (heading) heading.textContent = "Here's what we recommend.";
      if (intro) {
        const timingCopy =
          { morning: "a morning routine", night: "a night routine", both: "your morning and night routine" }[
            savedAnswers.timing
          ] || "your routine";
        intro.textContent = `Based on your answers, here's our pick for ${timingCopy}.`;
      }

      showStep("results");
      openQuiz();
    };

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get("customer_posted") === "true") {
      let savedAnswers = null;
      try {
        savedAnswers = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      } catch (error) {
        savedAnswers = null;
      }

      if (savedAnswers) {
        renderResults(savedAnswers);
        try {
          sessionStorage.removeItem(storageKey);
        } catch (error) {
          /* Nothing left to clean up if storage is unavailable. */
        }
      }

      currentUrl.searchParams.delete("customer_posted");
      window.history.replaceState({}, document.title, `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    }
  }

  document.querySelectorAll("[data-selivia-copy-button]").forEach((button) => {
    button.addEventListener("click", () => {
      const codeBox = button.closest("[data-selivia-copy-code]");
      const code = codeBox ? codeBox.dataset.seliviaCopyCode : "";
      if (!code) return;

      const restoreLabel = button.textContent;
      const onCopied = () => {
        button.textContent = "Copied!";
        window.setTimeout(() => {
          button.textContent = restoreLabel;
        }, 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(onCopied).catch(() => {});
      } else {
        const tempInput = document.createElement("textarea");
        tempInput.value = code;
        tempInput.style.position = "fixed";
        tempInput.style.opacity = "0";
        document.body.appendChild(tempInput);
        tempInput.select();
        try {
          document.execCommand("copy");
          onCopied();
        } catch (error) {
          /* Clipboard unavailable; user can still select and copy manually. */
        }
        tempInput.remove();
      }
    });
  });
})();
